// Risk Mapping API Service
// frontend/src/services/riskMappingAPI.ts

import { apiClient } from '../api';
import toastService from './toastService';

export interface RiskAssessmentResponse {
  success: boolean;
  message: string;
  data: {
    risk_assessments: any[];
    control_gaps: any;
    compliance_scores: any;
    risk_metrics: any;
    heatmap_data: any;
    recommendations: any;
    assessment_summary?: {
      total_processes: number;
      high_risk_count: number;
      critical_findings: number;
      frameworks_assessed: string[];
    };
  };
  generated_at: string;
}

export interface ProcessStepsResponse {
  success: boolean;
  data: {
    process_steps: Array<{
      name: string;
      description: string;
      owner: string;
      risk_count: number;
      highest_risk_score: number;
      average_risk_score: number;
      risks: Array<{
        id: number;
        category: string;
        category_code: string;
        inherent_risk: number;
        residual_risk: number;
        control_effectiveness: number;
        regulatory_citation: string;
      }>;
    }>;
    risk_categories: Array<{
      id: number;
      category_code: string;
      category_name: string;
      risk_type: string;
      description: string;
      regulatory_citation: string;
      severity_level: string;
      framework?: {
        name: string;
        version: string;
      };
    }>;
  };
}

export interface ControlGapsResponse {
  success: boolean;
  data: {
    missing_controls: number;
    weak_controls: number;
    strong_controls: number;
    control_gaps: {
      missing_controls: Array<{
        process_step: string;
        required_control: string;
        regulatory_citation: string;
        framework: string;
        severity: string;
        description: string;
      }>;
      weak_controls: Array<{
        process_step: string;
        current_effectiveness: number;
        required_effectiveness: number;
        improvement_needed: number;
        regulatory_impact: string;
      }>;
      regulatory_violations: Array<{
        type: string;
        severity: string;
        framework: string;
        citation: string;
        description: string;
        remediation_required: boolean;
        timeline: string;
      }>;
      recommendations: Array<{
        action: string;
        process_step: string;
        description: string;
        regulatory_requirement: string;
        priority: string;
        timeline: string;
        estimated_cost: string;
      }>;
    };
    summary: {
      total_processes: number;
      control_coverage_percentage: number;
      critical_gaps: number;
    };
  };
}

export interface RiskScoringResponse {
  success: boolean;
  data: {
    risk_matrix: number[][][]; // 5x5 matrix with risk items
    risk_scores: {
      overall_risk_score: number;
      control_coverage: number;
      automation_ratio: number;
    };
    compliance_scores: {
      [framework: string]: {
        score: number;
        compliant_controls: number;
        total_controls: number;
        status: string;
      };
    };
    risk_distribution: {
      total_risks: number;
      high_risk_count: number;
      medium_risk_count: number;
      low_risk_count: number;
    };
  };
}

export interface AnalyticsResponse {
  success: boolean;
  data: {
    overall_risk_score: number;
    control_coverage: number;
    automation_ratio: number;
    compliance_score: number;
    detailed_metrics: {
      high_risk_processes: number;
      critical_findings: number;
      total_processes: number;
    };
    compliance_breakdown: {
      [framework: string]: {
        score: number;
        status: string;
      };
    };
  };
}

export interface RecommendationsResponse {
  success: boolean;
  data: {
    immediate_actions: Array<{
      title: string;
      description: string;
      process_step: string;
      regulatory_requirement: string;
      priority: string;
      timeline: string;
      effort: string;
      benefits: string[];
      regulatory_citation: string;
    }>;
    short_term: Array<{
      title: string;
      description: string;
      priority: string;
      timeline: string;
      effort: string;
      benefits: string[];
    }>;
    long_term: Array<{
      title: string;
      description: string;
      priority: string;
      timeline: string;
      effort: string;
      benefits: string[];
    }>;
  };
}

class RiskMappingAPI {
  /**
   * Generate comprehensive risk assessment for a project
   */
  async generateAssessment(projectId: number, forceRegenerate: boolean = false): Promise<RiskAssessmentResponse> {
    try {
      const response = await apiClient.post(`api/risk-mapping/${projectId}/generate`, {
        forceRegenerate
      });
      toastService.success('Risk assessment generation started');
      return response.data;
    } catch (error: any) {
      console.error('Generate assessment error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to generate risk assessment';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * Get current risk assessment data for a project
   */
  async getRiskAssessment(projectId: number): Promise<RiskAssessmentResponse> {
    try {
      const response = await apiClient.get(`api/risk-mapping/${projectId}`);
      return response.data;
    } catch (error: any) {
      console.error('Get risk assessment error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to retrieve risk assessment';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * Get process steps with risk mappings for Risk Mapping tab
   */
  async getProcessSteps(projectId: number): Promise<ProcessStepsResponse> {
    try {
      const response = await apiClient.get(`api/risk-mapping/${projectId}/process-steps`);
      return response.data;
    } catch (error: any) {
      console.error('Get process steps error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to retrieve process steps';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * Get control gaps analysis for Control Gaps tab
   */
  async getControlGaps(projectId: number): Promise<ControlGapsResponse> {
    try {
      const response = await apiClient.get(`api/risk-mapping/${projectId}/control-gaps`);
      return response.data;
    } catch (error: any) {
      console.error('Get control gaps error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to retrieve control gaps';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * Get risk scoring and matrix data for Risk Scoring tab
   */
  async getRiskScoring(projectId: number): Promise<RiskScoringResponse> {
    try {
      const response = await apiClient.get(`api/risk-mapping/${projectId}/risk-scoring`);
      return response.data;
    } catch (error: any) {
      console.error('Get risk scoring error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to retrieve risk scoring';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * Get analytics dashboard data for Analytics tab
   */
  async getAnalytics(projectId: number): Promise<AnalyticsResponse> {
    try {
      const response = await apiClient.get(`api/risk-mapping/${projectId}/analytics`);
      return response.data;
    } catch (error: any) {
      console.error('Get analytics error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to retrieve analytics';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * Get recommendations with regulatory context for Recommendations tab
   */
  async getRecommendations(projectId: number): Promise<RecommendationsResponse> {
    try {
      const response = await apiClient.get(`api/risk-mapping/${projectId}/recommendations`);
      return response.data;
    } catch (error: any) {
      console.error('Get recommendations error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to retrieve recommendations';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * Update risk assessment for a specific process step
   */
  async updateAssessment(
    projectId: number, 
    assessmentId: number, 
    updateData: {
      inherent_probability?: number;
      inherent_impact?: number;
      control_effectiveness?: number;
      existing_controls?: string[];
      control_gaps?: any[];
      recommendations?: any[];
    }
  ): Promise<{ success: boolean; message: string; data: any }> {
    try {
      const response = await apiClient.put(`api/risk-mapping/${projectId}/assessment/${assessmentId}`, updateData);
      toastService.success('Risk assessment updated successfully');
      return response.data;
    } catch (error: any) {
      console.error('Update assessment error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to update risk assessment';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * Export risk assessment report
   */
  async exportAssessment(
    projectId: number, 
    format: 'pdf' | 'excel' | 'json' = 'pdf'
  ): Promise<Blob> {
    try {
      const response = await apiClient.get(`api/risk-mapping/${projectId}/export`, {
        params: { format },
        responseType: 'blob'
      });
      toastService.success('Risk assessment exported successfully');
      return response.data;
    } catch (error: any) {
      console.error('Export assessment error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to export risk assessment';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * Get regulatory frameworks available in the system
   */
  async getRegulatoryFrameworks(): Promise<{
    success: boolean;
    data: Array<{
      id: number;
      name: string;
      version: string;
      description: string;
      effective_date: string;
      requirements: any;
    }>;
  }> {
    try {
      const response = await apiClient.get('api/regulatory-frameworks');
      return response.data;
    } catch (error: any) {
      console.error('Get regulatory frameworks error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to retrieve regulatory frameworks';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * Get risk categories for a specific framework
   */
  async getRiskCategories(frameworkId?: number): Promise<{
    success: boolean;
    data: Array<{
      id: number;
      category_code: string;
      category_name: string;
      risk_type: string;
      description: string;
      regulatory_citation: string;
      severity_level: string;
    }>;
  }> {
    try {
      const params = frameworkId ? { framework_id: frameworkId } : {};
      const response = await apiClient.get('api/risk-categories', { params });
      return response.data;
    } catch (error: any) {
      console.error('Get risk categories error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to retrieve risk categories';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * Get control types available in the system
   */
  async getControlTypes(): Promise<{
    success: boolean;
    data: Array<{
      id: number;
      name: string;
      description: string;
      control_nature: string;
      automation_level: string;
      frequency: string;
    }>;
  }> {
    try {
      const response = await apiClient.get('api/control-types');
      return response.data;
    } catch (error: any) {
      console.error('Get control types error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to retrieve control types';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * Validate risk assessment data before submission
   */
  validateRiskData(riskData: {
    probability: number;
    impact: number;
    controlEffectiveness: number;
  }): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (riskData.probability < 1 || riskData.probability > 5) {
      errors.push('Probability must be between 1 and 5');
    }

    if (riskData.impact < 1 || riskData.impact > 5) {
      errors.push('Impact must be between 1 and 5');
    }

    if (riskData.controlEffectiveness < 1 || riskData.controlEffectiveness > 5) {
      errors.push('Control effectiveness must be between 1 and 5');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Calculate risk score based on probability and impact
   */
  calculateRiskScore(probability: number, impact: number): number {
    return probability * impact;
  }

  /**
   * Calculate residual risk based on inherent risk and control effectiveness
   */
  calculateResidualRisk(inherentRisk: number, controlEffectiveness: number): number {
    const mitigationFactor = (controlEffectiveness - 1) / 4; // Normalize to 0-1
    return Math.max(1, Math.round(inherentRisk * (1 - mitigationFactor)));
  }

  /**
   * Get risk level text based on score
   */
  getRiskLevel(riskScore: number): string {
    if (riskScore >= 20) return 'CRITICAL';
    if (riskScore >= 15) return 'HIGH';
    if (riskScore >= 9) return 'MEDIUM';
    if (riskScore >= 4) return 'LOW';
    return 'VERY LOW';
  }

  /**
   * Get risk level color class based on score
   */
  getRiskLevelColor(riskScore: number): string {
    if (riskScore >= 20) return 'bg-red-600 text-white';
    if (riskScore >= 15) return 'bg-orange-500 text-white';
    if (riskScore >= 9) return 'bg-yellow-500 text-black';
    if (riskScore >= 4) return 'bg-blue-500 text-white';
    return 'bg-green-500 text-white';
  }
}

// Export singleton instance
export const riskMappingAPI = new RiskMappingAPI();